/**
 * The studio's address, as a link that opens it in maps (D-499).
 *
 * <b>One component because two screens ask the same question.</b> The address
 * renders on the home page's practicalities panel and again in the public
 * footer, from one content key. Linking it in one place and not the other is
 * the drift this project keeps recording — and the footer is historically the
 * copy that misses the fix: the note three lines above where this is used in
 * <c>PublicShell</c> records D-396 repairing exactly that, after the portal's
 * Account card and the footer drew one content key two ways.
 *
 * <b>A search query rather than a place id.</b> Google's `?api=1` search URL is
 * documented and stable, it deep-links into the app on a phone and opens the
 * web map on a laptop, and — the part that matters here — it needs nothing
 * maintained. A place id would be a second thing to keep in step with the
 * address whenever the studio moves, and Tonya would have no way to fix it
 * (A-16's reasoning: she must never need a developer to change her own
 * details).
 */
export function mapsHref(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

export function StudioAddress({ address, className = '' }: { address: string; className?: string }) {
  // A blank key would otherwise link to a search for nothing. Both callers
  // guard with `has()` already; this is so a third one cannot forget.
  if (!address.trim()) return null

  return (
    <a
      href={mapsHref(address)}
      target="_blank"
      rel="noopener noreferrer"
      /*
        `inline-block` with padding, because the line box alone is under the
        24px the layout audit wants of anything tappable (D-255): 14px on
        `leading-relaxed` is 22.75px on the home page, and the footer's 12.5px
        inherits preflight's 1.5 for 18.75px.

        <b>`py-1` rather than `py-0.5`, and the footer is why.</b> Four pixels
        clears the home page (26.75px) and leaves the footer at 22.75 — still a
        finding, on every public route at every width, since the footer is on
        all of them. Caught by a cold audit doing the arithmetic the first
        version of this comment got wrong.
      */
      className={`inline-block py-1 text-accent-2 underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current ${className}`}
    >
      {address}
    </a>
  )
}
